import { React, PropTypes } from 'imports';
import { FormattedResource } from '..';
import { PeriodType } from 'utils/constants';
import Enum from 'utils/Enum';

// TODO split this component into two:
// - TimeStamp to mark comments, news etc, with "some days ago" functionality and some reasonable default behavior, probably without relation to PeriodType
// - Period (?) to use with Period class and detecting detalization ("year 2019"/"week 7"/"36.27.1999") automatically
// currently it tries to be both, and suck at both

const SECOND = 1000; // ms
const MINUTE = 60 * SECOND; // ms
const HOUR = 60 * MINUTE; // ms
const DAY = 24 * HOUR;
const MONTH = 31 * DAY; // not really, but does a day difference really matter in a context of "message posted 5 months ago"?
const YEAR = 365 * MONTH;

export { PeriodType };

export const Format = Enum('FULL', 'SHORT', 'NUMERIC', 'SCOPED');

const SETTINGS_BY_FORMAT = {
    [Format.FULL]: {
        [null]: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        },
        [PeriodType.DAY]: { year: 'numeric', month: 'long', day: '2-digit' },
        [PeriodType.WEEK]: null,
        [PeriodType.MONTH]: { year: 'numeric', month: 'long' },
        [PeriodType.QUARTER]: null,
        [PeriodType.YEAR]: { year: 'numeric' }
    },
    [Format.SHORT]: {
        [null]: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        },
        [PeriodType.DAY]: { year: 'numeric', month: 'short', day: '2-digit' },
        [PeriodType.WEEK]: null,
        [PeriodType.MONTH]: { year: 'numeric', month: 'short' },
        [PeriodType.QUARTER]: null,
        [PeriodType.YEAR]: { year: 'numeric' }
    },
    [Format.NUMERIC]: {
        [null]: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        },
        [PeriodType.DAY]: { year: 'numeric', month: '2-digit', day: '2-digit' },
        [PeriodType.WEEK]: null,
        [PeriodType.MONTH]: { year: 'numeric', month: 'numeric' },
        [PeriodType.QUARTER]: null,
        [PeriodType.YEAR]: { year: 'numeric' }
    },
    [Format.SCOPED]: {
        [null]: {
            year: 'numeric',
            month: 'numeric',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric'
        },
        [PeriodType.DAY]: { day: '2-digit' },
        [PeriodType.WEEK]: null,
        [PeriodType.MONTH]: { month: 'short' },
        [PeriodType.QUARTER]: null,
        [PeriodType.YEAR]: { year: 'numeric' }
    }
};

function toExactString(intl, date, detalizationPeriodType = PeriodType.DAY, format = Format.FULL, placeholder = { id: 'time.never' }, fiscalPeriodStartMonth) {
    format = format === true ? Format.SCOPED : (format || Format.FULL);

    if (date === null) {
        return typeof placeholder === 'string' ? placeholder : intl.formatMessage(placeholder);
    }

    switch (detalizationPeriodType) {
        case null:
        case PeriodType.DAY:
        case PeriodType.MONTH:
            return intl.formatDate(date, SETTINGS_BY_FORMAT[format][detalizationPeriodType]);

        case PeriodType.WEEK:
            const year = date.getUTCMonth() === 11 && date.getUTCWeek() === 1 ? date.getUTCFullYear() + 1 : date.getUTCFullYear();
            return intl.formatMessage({ id: format !== Format.NUMERIC ? 'indicator_data_preview.week' : 'indicator_data_preview.week_short' }, { weekNumber: date.getUTCWeek() + (format !== Format.SCOPED ? ' / ' + year : '') });

        case PeriodType.QUARTER:
            return intl.formatMessage({ id: 'indicator_data_preview.quarter' },
                {
                    quarterNumber: (((date.getUTCMonth() / 3) | 0) + 1) +
                        (format !== Format.SCOPED ? ' / ' + date.getUTCFullYear() + (!fiscalPeriodStartMonth ? '' : (' / ' + (date.getUTCFullYear() + 1))) : '')
                }
            );

        case PeriodType.YEAR:
            return intl.formatDate(date, SETTINGS_BY_FORMAT[format][detalizationPeriodType]) +
                (!fiscalPeriodStartMonth ? '' : (' / ' + intl.formatDate(new Date(new Date(date).setUTCFullYear(date.getUTCFullYear() + 1)), SETTINGS_BY_FORMAT[format][detalizationPeriodType])));

        default:
            throw new Error(`Unknown PeriodType: ${detalizationPeriodType}`);
    }
}

function toSimpleString(intl, date, exactDatePeriodType = PeriodType.DAY, detalizationPeriodType = PeriodType.DAY, format = Format.FULL, placeholder = { id: 'time.never' }, forceShowLessThanDayValues = false, fiscalPeriodStartMonth = 0) {
    format = format === true ? Format.SCOPED : (format || Format.FULL);

    if (!exactDatePeriodType) {
        return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
    }

    if (date === null) {
        return typeof placeholder === 'string' ? placeholder : intl.formatMessage(placeholder);
    }

    const now = new Date();
    const future = now <= date;

    if (!future) {
        const passed = now - date;

        if ((!detalizationPeriodType || forceShowLessThanDayValues) && passed < MINUTE) {
            return <FormattedResource resource={{ id: 'time.just_now' }} />;
        }

        if ((!detalizationPeriodType || forceShowLessThanDayValues) && passed < HOUR) {
            return <FormattedResource resource={{ id: 'time.minutes_ago' }} values={{ value: Math.floor(passed / MINUTE) }} />;
        }

        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);

        if ((!detalizationPeriodType || forceShowLessThanDayValues) && date >= todayStart) {
            return <FormattedResource resource={{ id: 'time.hours_ago' }} values={{ value: Math.floor(passed / HOUR) }} />;
        }

        if (exactDatePeriodType === PeriodType.DAY) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        }

        const lastMonthEnd = new Date(todayStart);
        lastMonthEnd.setMonth(todayStart.getMonth() - 1);

        if (detalizationPeriodType === PeriodType.DAY && date >= lastMonthEnd) {
            return <FormattedResource resource={{ id: 'time.days_ago' }} values={{ value: Math.floor((todayStart - date) / DAY) + 1 }} />;
        }

        if (exactDatePeriodType === PeriodType.MONTH || exactDatePeriodType === PeriodType.QUARTER) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        }

        const lastYearEnd = new Date(todayStart);
        lastYearEnd.setYear(todayStart.getYear() - 1);

        if ((detalizationPeriodType === PeriodType.DAY || detalizationPeriodType === PeriodType.QUARTER) && date >= lastYearEnd) {
            return <FormattedResource resource={{ id: 'time.months_ago' }} values={{ value: Math.floor(passed / MONTH) }} />;
        }

        if (exactDatePeriodType === PeriodType.YEAR) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        } else {
            return <FormattedResource resource={{ id: 'time.years_ago' }} values={{ value: Math.floor(passed / YEAR) }} />;
        }
    }
    else {
        const remaining = date - now;

        if ((!detalizationPeriodType || forceShowLessThanDayValues) && remaining < MINUTE) {
            return <FormattedResource resource={{ id: 'time.right_now' }} />;
        }
        if ((!detalizationPeriodType || forceShowLessThanDayValues) && remaining < HOUR) {
            return <FormattedResource resource={{ id: 'time.in_minutes' }} values={{ value: Math.ceil(remaining / MINUTE) }} />;
        }

        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 59);

        if (date <= todayEnd) {
            return <FormattedResource resource={{ id: 'time.in_hours' }} values={{ value: Math.ceil(remaining / HOUR) }} />;
        }

        if (exactDatePeriodType === PeriodType.DAY) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        }

        const nextMonthStart = new Date(todayEnd);
        nextMonthStart.setMonth(todayEnd.getMonth() + 1);

        if (detalizationPeriodType === PeriodType.DAY && date <= nextMonthStart) {
            return <FormattedResource resource={{ id: 'time.in_days' }} values={{ value: Math.ceil(remaining / DAY) }} />;
        }

        if (exactDatePeriodType === PeriodType.MONTH || exactDatePeriodType === PeriodType.QUARTER) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        }

        const nextYearStart = new Date(todayEnd);
        nextYearStart.setYear(todayEnd.getYear() - 1);

        if ((detalizationPeriodType === PeriodType.MONTH || detalizationPeriodType === PeriodType.QUARTER) && date <= nextYearStart) {
            return <FormattedResource resource={{ id: 'time.in_months' }} values={{ value: Math.ceil(remaining / MONTH) }} />;
        }

        if (exactDatePeriodType === PeriodType.YEAR) {
            return toExactString(intl, date, detalizationPeriodType, format, placeholder, fiscalPeriodStartMonth);
        } else {
            return <FormattedResource resource={{ id: 'time.in_years' }} values={{ value: Math.ceil(remaining / YEAR) }} />;
        }
    }
}

export const toString = toSimpleString;

export default class TimeStamp extends React.Component {
    static get PeriodType() {
        return PeriodType;
    }

    static get Format() {
        return Format;
    }

    static get toString() {
        return toString;
    }

    static get propTypes() {
        return {
            value: PropTypes.instanceOf(Date),
            placeholder: FormattedResource.resourcePropType,
            exactDatePeriodType: PropTypes.oneOf(Object.values(PeriodType)),
            detalizationPeriodType: PropTypes.oneOf(Object.values(PeriodType)),
            format: PropTypes.oneOf(Object.values(Format)),
            forceShowLessThanDayValues: PropTypes.bool,
            fiscalPeriodStartMonth: PropTypes.number
        };
    }

    static get defaultProps() {
        return {
            placeholder: { id: 'time.never' },
            exactDatePeriodType: PeriodType.MONTH,
            detalizationPeriodType: null,
            format: Format.FULL,
            forceShowLessThanDayValues: false,
            fiscalPeriodStartMonth: 0
        };
    }

    static get contextTypes() {
        return {
            intl: PropTypes.object.isRequired
        };
    }

    componentWillMount() {
        this._intervalID = setInterval(() => this.forceUpdate(), MINUTE);
    }

    componentWillUnmount() {
        clearInterval(this._intervalID);
    }

    render() {
        return (
            <span className="time-stamp" title={toExactString(this.context.intl, this.props.value)}>
                {toSimpleString(this.context.intl, this.props.value, this.props.exactDatePeriodType, this.props.detalizationPeriodType, this.props.short !== undefined ? this.props.short : this.props.format, this.props.placeholder, this.props.forceShowLessThanDayValues, this.props.fiscalPeriodStartMonth)}
            </span>
        );
    }
}